import { Box, type ButtonProps, Typography } from '@mui/material'
import React from 'react'
import { useQueryClient } from 'react-query'
import { useBoolean } from 'react-use'

import { ButtonWithConfirmation } from 'components/common/ButtonWithConfirmation'
import Icon from 'components/Icon'
import { getDeleteExternalIntegrationsStatusCheckAuthenticationParams } from 'components/status-checks/status-checks-form-utils'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { externalIntegrationsQueryKeys } from 'queries/external-integrations/query-keys'
import { useDeleteExternalIntegrationsStatusChecksAuthentication } from 'queries/external-integrations/use-delete-external-integrations-status-checks-authentication'
import { type TeamExternalIntegration } from 'types/api/status-checks/status-check'
import { messageFromResponseError } from 'utils/strings'

/**
 * Affords a button to delete/revoke a status check integration's authentication.
 *
 * Note that this component is specifically for use with TeamExternalIntegration authentications that support status
 * checks, not load generators.
 */
export const DeleteStatusCheckAuthentication: React.FC<{
  integration: TeamExternalIntegration | undefined
  onDeleteComplete?: () => void
  buttonProps?: ButtonProps
}> = ({ buttonProps, integration, onDeleteComplete }) => {
  const [isSubmitting, setIsSubmitting] = useBoolean(false)
  const { currentTeamId } = useAuth()
  const queryClient = useQueryClient()

  const deleteExternalIntegrationsStatusCheckAuthenticationParams =
    getDeleteExternalIntegrationsStatusCheckAuthenticationParams(integration)

  const { mutateAsync: deleteExternalIntegrationsStatusChecksAuthentication } =
    useDeleteExternalIntegrationsStatusChecksAuthentication(
      deleteExternalIntegrationsStatusCheckAuthenticationParams
    )

  const handleDeleteIntegration = async ({ setIsModalOpen }) => {
    setIsSubmitting(true)

    try {
      await deleteExternalIntegrationsStatusChecksAuthentication()

      toast({
        type: 'success',
        text: 'Successfully revoked Authentication'
      })

      setIsSubmitting(false)

      // Close modal prior to calling onChanged to prevent state changes on an unmounted component
      setIsModalOpen(false)

      if (onDeleteComplete) {
        onDeleteComplete()
      }

      await queryClient.invalidateQueries(
        externalIntegrationsQueryKeys.getStatusCheckIntegrations({
          teamId: currentTeamId
        })
      )
    } catch (err) {
      toast({
        type: 'error',
        text: messageFromResponseError(
          err,
          'There was a problem while removing the team integration. Please try again.'
        )
      })

      setIsSubmitting(false)
      setIsModalOpen(false)
    }
  }

  return (
    <Box>
      <ButtonWithConfirmation
        buttonConfirmProps={{
          color: 'error',
          loading: isSubmitting,
          startIcon: <Icon icon="trash" />
        }}
        buttonConfirmText="Revoke"
        color="error"
        dialogBodyContent={
          <Typography>
            Are you sure you want to revoke this authentication? This will
            revoke the authentication on this team.
          </Typography>
        }
        dialogTitleContent="Revoke Authentication?"
        disabled={!integration}
        size="small"
        startIcon={<Icon icon="trash" />}
        variant="contained"
        onConfirm={handleDeleteIntegration}
        {...buttonProps}
      >
        Revoke
      </ButtonWithConfirmation>
    </Box>
  )
}
